// src/pages/Reports/IssuanceReporting/PeriodSummaryClosing/PeriodClosingTab.tsx
// RPT-018 (README.md mục 7) — chốt kỳ xổ, có workflow xác nhận. Hiển thị trạng thái hiện tại
// (đã chốt / chưa chốt) + snapshot số liệu tại thời điểm chốt (nếu đã chốt).

import React, { useCallback, useEffect, useState } from 'react';
import { Button, Col, Row, Spinner } from 'reactstrap';
import { toast } from 'react-toastify';

import { getPeriodClosingApi } from 'helpers/fakebackend_helper';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { useLocation } from 'react-router-dom';
import IssuancePlanSelect from '../shared/IssuancePlanSelect';
import { formatCurrency, formatDateTime, formatQty } from '../shared/formatters';
import { PeriodClosing } from '../shared/types';
import ClosePeriodModal from './ClosePeriodModal';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('reports_issuance_reporting');

interface PeriodClosingTabProps {
  planId: string;
  onPlanIdChange: (value: string) => void;
}

const PeriodClosingTab: React.FC<PeriodClosingTabProps> = ({ planId, onPlanIdChange }) => {
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState<PeriodClosing | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const loadClosing = useCallback(() => {
    if (!planId) {
      setClosing(null);
      return;
    }
    setLoading(true);
    getPeriodClosingApi(Number(planId))
      .then((res) => setClosing((res as any)?.data ?? res))
      .catch((error: any) => toast.error(error?.message || st('khong_the_tai_trang_thai_chot_message')))
      .finally(() => setLoading(false));
  }, [planId]);

  useEffect(() => { loadClosing(); }, [loadClosing]);

  const isClosed = closing?.status === 2;

  return (
    <>
      <Row className="g-3 mb-4 align-items-end">
        <Col md={4}>
          <label className="form-label">{st('ky_phat_hanh_label')}</label>
          <IssuancePlanSelect requireSelection value={planId} onChange={onPlanIdChange} />
        </Col>
        {planId && !isClosed && PageActionPermission.canDisplay(location.pathname, 'button-close-period') && (
          <Col md="auto">
            <Button
              color="danger"
              data-action-code="button-close-period"
              data-action-name="Chốt kỳ phát hành"
              disabled={loading}
              onClick={() => setModalOpen(true)}
            >
              <i className="ri-lock-line align-bottom me-1"></i>
              {st('chot_ky_label')}
            </Button>
          </Col>
        )}
      </Row>

      {!planId ? (
        <div className="text-center text-muted py-5">{st('vui_long_chon_ky_phat_hanh_text')}</div>
      ) : loading ? (
        <div className="text-center py-5"><Spinner color="primary" /></div>
      ) : closing ? (
        <div className="border rounded p-4">
          <div className="mb-3 d-flex align-items-center gap-2">
            <strong>{closing.planCode}</strong>
            <span className={`badge bg-${isClosed ? 'success' : 'warning'}-subtle text-${isClosed ? 'success' : 'warning'}`}>
              {closing.statusName}
            </span>
          </div>

          <Row className="g-3 mb-3">
            <Col md={3}><strong>{st('dai_ly_da_doi_soat_text')}</strong> {closing.agentsReconciledCount}</Col>
            <Col md={3}><strong>{st('chua_doi_soat_text_2')}</strong> {closing.agentsNotReconciledCount}</Col>
            <Col md={3}><strong>{st('co_chenh_lech_text_2')}</strong> {closing.agentsWithDiscrepancyCount}</Col>
          </Row>

          {isClosed && (
            <>
              <Row className="g-3">
                <Col md={3}><strong>{st('tong_phat_hanh_text')}</strong> {formatQty(closing.totalIssuedQuantity)}</Col>
                <Col md={3}><strong>{st('da_ban_text')}</strong> {formatQty(closing.totalSoldQuantity)}</Col>
                <Col md={3}><strong>{st('thu_hoi_text')}</strong> {formatQty(closing.totalRecalledQuantity)}</Col>
                <Col md={3}><strong>{st('chenh_lech_text')}</strong> {formatQty(closing.totalDiscrepancyQuantity)}</Col>
                <Col md={3}><strong>{st('gia_tri_phat_hanh_text')}</strong> {formatCurrency(closing.totalIssuedValue)}</Col>
                <Col md={3}><strong>{st('gia_tri_da_ban_text')}</strong> {formatCurrency(closing.totalSoldValue)}</Col>
              </Row>
              {closing.forceClose && (
                <div className="alert alert-warning mt-3 mb-0">
                  <strong>{st('da_chot_cuong_che_text')}</strong> {st('ly_do_text')} {closing.forceCloseReason}
                </div>
              )}
              <div className="text-muted mt-3">
                {st('chot_luc_text')} {formatDateTime(closing.closedAt)}
              </div>
            </>
          )}
        </div>
      ) : null}

      <ClosePeriodModal
        isOpen={modalOpen}
        planId={Number(planId)}
        closing={closing}
        onClose={() => setModalOpen(false)}
        onClosed={() => { setModalOpen(false); loadClosing(); }}
      />
    </>
  );
};

export default PeriodClosingTab;
